import React, { useState } from 'react'
import { supabase } from '../lib/supabase'

// Lead-capture only -- calls submit_trial_request() (SECURITY DEFINER,
// granted to anon), which inserts into trial_requests and nothing else.
// No account, organization, or subscription is created here or by that
// function. Visually kept close to CostSpaceLanding's own design
// language (navy #0B3D91, rounded cards, same font stack) without
// touching CostSpaceLanding.jsx's own markup/CSS.
const BRAND_NAVY = '#0B3D91'

export default function TrialRequestModal({ open, onClose }) {
  const [form, setForm] = useState({ full_name: '', company_name: '', project_name: '', project_summary: '', email: '' })
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState(null)

  if (!open) return null

  async function handleSubmit(e) {
    e.preventDefault()
    if (form.project_summary.trim().length < 20) {
      setError('Please describe your project in at least 20 characters.')
      return
    }
    setSubmitting(true)
    setError(null)
    const { data: requestId, error: err } = await supabase.rpc('submit_trial_request', {
      p_full_name: form.full_name,
      p_company_name: form.company_name,
      p_project_name: form.project_name,
      p_project_summary: form.project_summary,
      p_email: form.email,
    })
    setSubmitting(false)
    if (err) { setError(err.message); return }
    setDone(true)
    // The database row is already the source of truth (it just
    // succeeded above) -- this notification email is a best-effort
    // convenience for the admin, not something the user's submission
    // depends on. A failure here is deliberately swallowed (not shown
    // to the user, not blocking `done`): the request itself was saved
    // successfully regardless, and it's still fully visible in
    // Subscription Management -> Trial Requests either way.
    supabase.functions.invoke('send-trial-request-notification', {
      body: { request_id: requestId },
    }).catch(() => {})
  }

  function handleClose() {
    setForm({ full_name: '', company_name: '', project_name: '', project_summary: '', email: '' })
    setDone(false)
    setError(null)
    onClose()
  }

  return (
    <div
      onClick={handleClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(11,31,58,.55)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'system-ui, -apple-system, sans-serif' }}
    >
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, padding: 32, position: 'relative' }}>
        <button onClick={handleClose} aria-label="Close" style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 18, color: '#9CA3AF', cursor: 'pointer' }}>✕</button>

        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: '#0F1017' }}>Request received</h3>
            <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
              Thank you — our team will reach out to set up your CostSpace trial shortly.
            </p>
          </div>
        ) : (
          <>
            <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, color: '#0F1017' }}>Start Your Free Trial</h3>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>Tell us about your project and we'll set you up.</p>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { key: 'full_name', label: 'Full Name', type: 'text' },
                { key: 'company_name', label: 'Company Name', type: 'text' },
                { key: 'project_name', label: 'Project Name', type: 'text' },
                { key: 'project_summary', label: 'Project Summary (what is the project about?)', type: 'textarea' },
                { key: 'email', label: 'Email Address', type: 'email' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea
                      required rows={4} minLength={20} maxLength={2000} value={form[f.key]}
                      placeholder="e.g. 12-storey residential tower in Riyadh, concrete frame, tendering stage..."
                      onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  ) : (
                    <input
                      required type={f.type} value={form[f.key]}
                      onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, outline: 'none' }}
                    />
                  )}
                </div>
              ))}
              {error && <p style={{ fontSize: 12, color: '#DC2626' }}>{error}</p>}
              <button type="submit" disabled={submitting}
                style={{ marginTop: 6, background: BRAND_NAVY, color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                {submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
